import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, ExternalLink, MessageCircle, X } from 'lucide-react';
import { RedeemCode } from '../components';
import { useTelegram } from '../hooks';

const TELEGRAM_CHANNEL = 'https://t.me/gemifaucet';
const CHANNEL_CARD_HIDDEN_KEY = 'gemifaucet_channel_card_hidden';
const PENDING_REDEEM_CODE_KEY = 'gemifaucet_pending_redeem_code';

export function RedeemPage() {
  const { openTelegramLink, hapticFeedback } = useTelegram();
  const [isChannelCardHidden, setIsChannelCardHidden] = useState(() => {
    return localStorage.getItem(CHANNEL_CARD_HIDDEN_KEY) === 'true';
  });
  
  // Check for pending redeem code from deep link
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  
  useEffect(() => {
    const code = sessionStorage.getItem(PENDING_REDEEM_CODE_KEY);
    if (code) {
      setPendingCode(code);
      // Clear from session storage after reading
      sessionStorage.removeItem(PENDING_REDEEM_CODE_KEY);
    }
  }, []);

  const handleHideChannelCard = () => {
    hapticFeedback('light');
    setIsChannelCardHidden(true);
    localStorage.setItem(CHANNEL_CARD_HIDDEN_KEY, 'true');
  };

  const handleJoinChannel = () => {
    hapticFeedback('medium');
    openTelegramLink(TELEGRAM_CHANNEL);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', bounce: 0.5, delay: 0.1 }}
          className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30"
        >
          <Gift className="w-8 h-8 text-white" />
        </motion.div>
        <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
          Redeem Codes
        </h1>
        <p className="text-white/60 text-sm mt-1">
          Enter promo codes to claim exclusive rewards
        </p>
      </div>

      {/* Telegram Channel Card - Beautiful Design */}
      <AnimatePresence>
        {!isChannelCardHidden && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            transition={{ delay: 0.2 }}
            className="relative overflow-hidden rounded-2xl"
          >
            {/* Close Button */}
            <button
              onClick={handleHideChannelCard}
              className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/50 transition-colors"
              aria-label="Hide channel card"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0088cc] via-[#00a0dc] to-[#0088cc] opacity-90" />
        
            {/* Animated Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white/20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-2xl animate-pulse" />
              <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/20 rounded-full translate-x-1/2 translate-y-1/2 blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
            </div>

            {/* Content */}
            <div className="relative p-5">
              <div className="flex items-center gap-4">
                {/* Telegram Icon */}
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" className="w-8 h-8 text-white" fill="currentColor">
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                  </svg>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg">Join Our Channel</h3>
                  <p className="text-white/80 text-sm">
                    Get exclusive promo codes & updates!
                  </p>
                </div>
              </div>

              {/* Join Button */}
              <motion.button
                onClick={handleJoinChannel}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full mt-4 py-3 px-4 bg-white text-[#0088cc] rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-white/95 transition-colors"
              >
                <MessageCircle className="w-5 h-5" />
                Join @gemifaucet
                <ExternalLink className="w-4 h-4 ml-1" />
              </motion.button>

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 mt-4 text-white/80 text-sm">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span>Active Community</span>
                </div>
                <div className="flex items-center gap-1">
                  <Gift className="w-4 h-4" />
                  <span>Daily Codes</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Redeem Code Component */}
      <RedeemCode initialCode={pendingCode} />

      {/* Tips Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20"
      >
        <h3 className="font-bold text-amber-400 mb-2 flex items-center gap-2">
          <span className="text-lg">💡</span>
          Pro Tips
        </h3>
        <ul className="space-y-2 text-sm text-white/70">
          <li className="flex items-start gap-2">
            <span className="text-amber-400">•</span>
            <span>Follow our Telegram channel for daily promo codes</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400">•</span>
            <span>Codes are released during special events & giveaways</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400">•</span>
            <span>Each code can only be used once per account</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-400">•</span>
            <span>Some codes have limited redemptions - be quick!</span>
          </li>
        </ul>
      </motion.div>
    </div>
  );
}
